import React, { useState, useEffect } from "react";
import { Typography } from "../../../../../components/__common/Typography/Typography";
import { Container } from "../../../../../components/Grid/Grid";
import Tabs from "../../../../../components/__common/_controls/Tab/Tab";
import LearnMoreMap from "./LearnMoreMap";

import {
  CountriesWithLocalPrivacy,
  HubRegions,
  MapRegions,
} from "../../../../../data/landing-page";
import { ERegions, MapRegion } from "../../../../../types/landing-page";
import PrivacyRightsModal from "../../PrivacyRightsModal/PrivacyRightsModal";
import { useTranslation } from "react-i18next";
import "./LearnMore.scss";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../../../store";
import { UserState } from "../../../../../store/reducers/user-reducer";
import FocusTrapping from "../../../../../components/__hoc/FocusTrapping";
import { LayoutState } from "../../../../../store/reducers/layout-reducer";
import { TTSButton } from "../../../../../components/__common/_controls";

const LearnMore: React.FC = () => {
  // third party hooks
  const { t } = useTranslation();

  // states
  const { userCountryCode } = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );
  const { selectedCountry } = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const [showPrivacyRightsModal, setShowPrivacyRightsModal] =
    useState<boolean>(false);
  const [selectedRegion, setSelectedRegion] = useState<MapRegion | undefined>(
    undefined
  );

  // variables
  const countryCode = selectedCountry.code ?? userCountryCode;
  const hasLocalSite = CountriesWithLocalPrivacy.some(
    (region) => region.code === countryCode
  );

  const handleToggleModal = () => {
    setShowPrivacyRightsModal(!showPrivacyRightsModal);
    document.getElementById(`${selectedRegion?.code}-map-button`)?.focus();
  };

  const getMapRegion = () => {
    let landingPageRegion = MapRegions.find(
      (region) => region.code === countryCode
    );

    const region = HubRegions.filter((region) =>
      region.countries.some((country) => country.code === countryCode)
    )[0];

    // Global countries with no local sites and added EU countries except turkey
    if (!hasLocalSite) {
      setSelectedRegion(MapRegions[0]);
      return;
    }

    // if specific country (e.g. US, China, Turkey)
    if (landingPageRegion) {
      setSelectedRegion(landingPageRegion);
      return;
    }
    // if specific region
    if (
      region.code === ERegions.LATIN_AMERICA ||
      region.code === ERegions.MENA ||
      region.code === ERegions.ASIA_PACIFIC
    ) {
      landingPageRegion = MapRegions.find(
        (hubRegion) => hubRegion.code === region.code
      );
      setSelectedRegion(landingPageRegion);
    }
  };

  useEffect(() => {
    if (countryCode || userCountryCode) {
      getMapRegion();
    }
  }, [userCountryCode, countryCode]);

  return (
    <>
      <div className="learn-more" id="learn-more">
        <Container>
          <Typography variant="h1" className="learn-more__title sharp-sans">
            {t("PRIVACY_HUB_LEARN_MORE_TITLE")}
            <TTSButton
              value={
                t("PRIVACY_HUB_LEARN_MORE_TITLE") +
                ". " +
                t("PRIVACY_HUB_LEARN_MORE_DESC")
              }
            />
          </Typography>
          <Typography
            variant="title3"
            className="learn-more__subtitle subText"
            weight="wMedium"
          >
            {t("PRIVACY_HUB_LEARN_MORE_DESC")}
          </Typography>
          <Tabs className="learn-more__tabs">
            {MapRegions.map((region, i) => (
              <Tabs.Tab
                radioTestId="region-radio"
                key={region.code}
                name="regions_tab"
                title={
                  region.name === "Global"
                    ? t("PRIVACY_HUB_LEARN_MORE_OTHER_REGIONS")
                    : region.name
                }
                type="2depth"
                checked={selectedRegion?.code === region.code}
                onChange={() => setSelectedRegion(region)}
              />
            ))}
          </Tabs>
          <Container className="learn-more__map svg">
            <LearnMoreMap
              testId="toggle-modal-anchor"
              code={selectedRegion?.code}
              handleToggleModal={handleToggleModal}
            />
          </Container>
        </Container>
      </div>
      {showPrivacyRightsModal && selectedRegion && (
        <FocusTrapping
          onHandleClose={handleToggleModal}
          isActive={showPrivacyRightsModal}
          modalId="privacy-rights-modal"
        >
          <PrivacyRightsModal
            show={showPrivacyRightsModal}
            onClickClose={handleToggleModal}
            country={selectedRegion}
          />
        </FocusTrapping>
      )}
    </>
  );
};

export default LearnMore;
